import _ from "lodash";
import {useMemo, useState} from "react";
import {useAtom} from "jotai";
import {useTranslation} from "react-i18next";
import {TFunction} from "i18next";
import {Link} from "react-router-dom";
import {Box, IconButton, Stack, Typography, TypographyProps} from "@mui/material";
import {
    DropdownOption,
    MRT_ColumnDef,
    MRT_ExpandButton,
    MRT_Row,
    MaterialReactTable,
    useMaterialReactTable,
} from "material-react-table";
import {getDefaultMRTOptions} from "../base/BaseTable";
import {FarmInsightItem, FieldInsightItem} from "../../types/fieldInsightItem";
import {FarmCreateDialog} from "../dialogs/FarmCreateDialog";
import {StatusIndicator} from "../base/StatusIndicator";
import {FieldStatus} from "../../types/domain/fieldStatus";
import {FieldEditDialog} from "../dialogs/FieldEditDialog";
import {EditIcon} from "../icons/EditIcon";
import {WaterIcon} from "../icons/WaterIcon";
import {CropIcon} from "../icons/CropIcon";
import {useMeasureSystemUtils} from "../../hooks/measureSystemUtilsHooks";
import {MeasureSystemUtils} from "../../types/domain/measureSystemUtils";
import {authenticatedUserTableLocaleAtom} from "../../store/authenticatedUserStore";
import {OrderStatus} from "../../types/domain/orderStatus";
import companyIdForRouting from "../../utils/companyRoutingUtils";

export interface Props {
    insightsItems: (FarmInsightItem | FieldInsightItem)[];
    companyId?: string;
    onRefresh: () => void;
    canEditItem: boolean;
}

export const FieldsInsightsTable: React.FC<Props> = (props) => {
    const {t} = useTranslation();
    const msUtils = useMeasureSystemUtils();
    const {insightsItems, companyId, onRefresh, canEditItem} = props;
    const [editDialogOpened, setEditDialogOpened] = useState<boolean>(false);
    const [activeInsightItem, setActiveInsightItem] = useState<FarmInsightItem | FieldInsightItem | undefined>(
        undefined,
    );
    const [locale] = useAtom(authenticatedUserTableLocaleAtom);
    const defaultTableOptions = getDefaultMRTOptions<FarmInsightItem | FieldInsightItem>(locale);
    const onEditDialogClose = (isSuccessful: boolean) => {
        setEditDialogOpened(false);
        if (isSuccessful) {
            onRefresh();
        }
    };
    const columns = useMemo(() => {
        const allAlertsLabel = t("insights.field_insights_table.alert_all");
        const cropsOptions = _.uniq(
            insightsItems
                .flatMap((item) => (item.type === "FIELD" ? item.crop : item.crops))
                .filter((item) => item)
                .map((item) => _.capitalize(item)),
        );

        return getColumns([{label: allAlertsLabel, value: "All"}, ...cropsOptions], t, msUtils, companyId);
    }, [insightsItems, t, msUtils, companyId]);

    const table = useMaterialReactTable({
        ...defaultTableOptions,
        enableRowVirtualization: true,
        initialState: {
            ...defaultTableOptions.initialState,
            columnFilters: [
                {
                    id: "moisture",
                    value: "All",
                },
                {
                    id: "growth",
                    value: "All",
                },
                {
                    id: "crop",
                    value: "All",
                },
            ],
        },
        muiTableBodyRowProps: ({row}) => ({
            sx: {
                backgroundColor: row.original.type === "FARM" ? "#F3F5FA" : "#FFFFFF",
                height: row.original.type === "FARM" ? "3.375rem !important" : "2.625rem !important",
            },
        }),
        muiTableBodyCellProps: ({row}) => ({
            sx: {
                paddingTop: "0 !important",
                paddingBottom: "0 !important",
                borderBottomWidth: row.original.type === "FARM" ? 1 : 0,
            },
        }),
        muiExpandButtonProps: ({row}) => ({
            sx: {
                transform: `rotate(${getExpandIconRotation(row)}deg)`,
                transition: "transform 150ms ease 0s",
            },
        }),
        columns,
        data: insightsItems,
        enableExpandAll: false,
        enableExpanding: true,
        enablePagination: false,
        enableBottomToolbar: false,
        positionPagination: "none",
        filterFromLeafRows: true,
        getRowId: (originalRow) =>
            originalRow.type === "FARM" ? `farm-${originalRow.farm.id}` : `field-${originalRow.field.id}`,
        getSubRows: (row) => (row.type === "FARM" ? row.fields : undefined),
        displayColumnDefOptions: {
            "mrt-row-expand": {
                enableResizing: false,
                minSize: 30,
                header: "",
                muiTableHeadCellProps: {
                    sx: {
                        minWidth: "0px !important",
                        width: "0px !important",
                        maxWidth: "3rem !important",
                        backgroundColor: "#F3F5FA",
                        paddingRight: "0 !important",
                    },
                },
                muiTableBodyCellProps: ({row}) => ({
                    sx: {
                        paddingLeft: "0.75rem !important",
                        paddingRight: "0 !important",
                        paddingY: 0,
                        borderBottomWidth: row.original.type === "FARM" ? 1 : 0,
                        minWidth: "0px !important",
                        width: "0px !important",
                        maxWidth: "3rem !important",
                    },
                }),
                Cell: ({row, table}) =>
                    row.original.type === "FARM" ? <MRT_ExpandButton row={row} table={table} /> : null,
            },
            "mrt-row-actions": {
                enableResizing: false,
                size: 30,
                header: "",
            },
        },
        renderRowActions: ({row}) =>
            canEditItem &&
            !isRowDefaultFarm(row.original) && (
                <Box sx={{display: "flex", gap: "1rem"}}>
                    <IconButton
                        color="primary"
                        onClick={() => {
                            setActiveInsightItem(row.original);
                            setEditDialogOpened(true);
                        }}
                    >
                        <EditIcon
                            sx={{
                                stroke: "#6476CB",
                                width: "1.125rem",
                                height: "1.125rem",
                            }}
                        />
                    </IconButton>
                </Box>
            ),
    });

    return (
        <>
            <MaterialReactTable table={table} />
            {editDialogOpened && companyId && activeInsightItem?.type === "FARM" && (
                <FarmCreateDialog
                    mode="edit"
                    companyId={companyId}
                    selectedFarm={activeInsightItem.farm}
                    open={editDialogOpened}
                    onClose={onEditDialogClose}
                />
            )}
            {editDialogOpened && companyId && activeInsightItem?.type === "FIELD" && (
                <FieldEditDialog
                    companyId={companyId}
                    selectedField={activeInsightItem.field}
                    open={editDialogOpened}
                    onClose={onEditDialogClose}
                />
            )}
        </>
    );
};

const getColumns = (
    cropsOptions: DropdownOption[],
    t: TFunction<string, undefined>,
    msUtils: MeasureSystemUtils,
    activeCompanyId?: string,
): MRT_ColumnDef<FarmInsightItem | FieldInsightItem>[] => {
    const allAlertsLabel = t("insights.field_insights_table.alert_all");
    const alertsLabel = t("insights.field_insights_table.alert_alerts");
    const noAlertsLabel = t("insights.field_insights_table.alert_no_alerts");
    const ALERT_OPTIONS = [{label: allAlertsLabel, value: "All"}, alertsLabel, noAlertsLabel];
    return [
        {
            id: "name",
            header: t("insights.field_insights_table.field"),
            accessorKey: "name",
            Cell: ({row, renderedCellValue}) => getNameLabel(row.original, renderedCellValue, activeCompanyId),
            minSize: 100,
        },
        {
            id: "date_next_irrigation",
            header: t("insights.field_insights_table.date_next_irrigation"),
            accessorKey: "dateNextIrrigation",
            maxSize: 100,
            enableColumnFilter: false,
            accessorFn: (row) => {
                if (row.type === "FIELD") {
                    return calculateDaysUntilNextIrrigation(row);
                }
                return null;
            },
            Cell: ({row}) => {
                const original = row.original;
                if (original.type === "FIELD") {
                    return getNextIrrigationDate(original, t);
                }
                return null;
            },
            sortingFn: "datetime",
        },
        {
            id: "moisture",
            header: t("insights.field_insights_table.moisture"),
            accessorKey: "moisture",
            Cell: ({row}) => getMoistureLabel(row.original, t),
            size: 50,
            filterVariant: "select",
            filterSelectOptions: ALERT_OPTIONS,
            filterFn: (row, _, filter) =>
                filter === "All" ||
                (row.original.type === "FIELD" &&
                    ((filter === alertsLabel && row.original.isMoistureAlert === true) ||
                        (filter === noAlertsLabel && row.original.isMoistureAlert === false))),
        },
        {
            id: "growth",
            header: t("insights.field_insights_table.growth"),
            accessorKey: "growth",
            Cell: ({row}) => getGrowthLabel(row.original, alertsLabel),
            size: 50,
            filterVariant: "select",
            filterSelectOptions: ALERT_OPTIONS,
            filterFn: (row, _, filter) =>
                filter === "All" ||
                (row.original.type === "FIELD" &&
                    ((filter === alertsLabel && row.original.isGrowthAlert === true) ||
                        (filter === noAlertsLabel && row.original.isGrowthAlert === false))),
        },
        {
            id: "crop",
            header: t("insights.field_insights_table.crop"),
            accessorKey: "crop",
            Cell: ({row}) => getCropLabel(row.original),
            minSize: 100,
            filterVariant: "select",
            filterSelectOptions: cropsOptions,
            filterFn: (row, _, filter) =>
                filter === "All" ||
                (row.original.type === "FIELD" &&
                    row.original.crop?.toLocaleLowerCase() === (filter as string).toLocaleLowerCase()),
        },
        {
            id: "area",
            header: t("insights.field_insights_table.area"),
            Cell: ({row}) => getAreaLabel(row.original, msUtils),
            muiTableBodyCellProps: {
                align: "right",
            },
            muiTableHeadCellProps: {
                align: "right",
            },
            size: 50,
            enableColumnFilter: false,
        },
    ];
};

const FARM_TYPOGRAPHY_STYLE: TypographyProps = {
    color: "#82889E",
    fontWeight: 500,
    fontSize: 14,
    textTransform: "uppercase",
    textOverflow: "ellipsis",
    noWrap: true,
};

const FIELD_TYPOGRAPHY_STYLE: TypographyProps = {
    color: "#545A6E",
    fontWeight: 400,
    fontSize: 14,
    textTransform: "capitalize",
    textOverflow: "ellipsis",
    noWrap: true,
};

const FIELD_DRAFT_TYPOGRAPHY_STYLE: TypographyProps = {
    color: "#ACB0C0",
    fontWeight: 400,
    fontSize: 14,
    textTransform: "capitalize",
    textOverflow: "ellipsis",
    noWrap: true,
};

const getNameLabel = (
    row: FarmInsightItem | FieldInsightItem,
    renderedValue: React.ReactNode,
    activeCompanyId?: string,
) => {
    const linkCompany =
        row.type === "FARM"
            ? companyIdForRouting(row.farm.companyId, activeCompanyId)
            : companyIdForRouting(row.field.companyId, activeCompanyId);

    return (
        <Stack direction="row" gap="0.5rem" alignItems="center">
            <Link
                style={{textDecoration: "none"}}
                to={
                    row.type === "FARM"
                        ? `/insights/${linkCompany}/farm/${row.farm.id}`
                        : `/insights/${linkCompany}/field/${row.field.id}`
                }
            >
                <Typography
                    {...(row.type === "FARM"
                        ? {...FARM_TYPOGRAPHY_STYLE, color: "#656B80"}
                        : {...FIELD_TYPOGRAPHY_STYLE, color: "#303A8C", textTransform: "none"})}
                    fontWeight={500}
                >
                    {renderedValue}
                </Typography>
            </Link>
            {row.type === "FIELD" && row.field.status === FieldStatus.DRAFT && (
                <Typography fontSize={12} bgcolor="#E9EBF1" paddingX="0.25rem" paddingY="0.125rem">
                    DRAFT
                </Typography>
            )}
        </Stack>
    );
};

const getMoistureLabel = (row: FarmInsightItem | FieldInsightItem, t: TFunction) => {
    if (row.type === "FARM") {
        return (
            <Stack direction="row" gap="0.4rem" alignItems="center">
                <StatusIndicator status={row.moistureAlerts > 0 ? "moistureAlert" : "normal"} />
                <Typography
                    {...FARM_TYPOGRAPHY_STYLE}
                >{`${row.moistureAlerts} ${t("insights.field_insights_table.alert_alerts")}`}</Typography>
            </Stack>
        );
    } else {
        return (
            <Stack direction="row">
                {row.growth !== null && (
                    <Stack
                        direction="row"
                        gap="0.4rem"
                        alignItems="center"
                        bgcolor={row.isMoistureAlert ? "#FDBBB1" : undefined}
                        paddingLeft="0.5rem"
                        paddingRight="1rem"
                        paddingY="0.15rem"
                        borderRadius={1}
                    >
                        <WaterIcon
                            sx={{
                                stroke: row.isMoistureAlert ? "#460A08" : "#5161C1",
                                width: "0.9rem",
                                height: "0.9rem",
                            }}
                        />
                        <Typography {...FARM_TYPOGRAPHY_STYLE}>{`${Number(row.moisture).toFixed(0)}%`}</Typography>
                    </Stack>
                )}
            </Stack>
        );
    }
};

const getGrowthLabel = (row: FarmInsightItem | FieldInsightItem, alertsLabel: string) => {
    if (row.type === "FARM") {
        return (
            <Stack direction="row" gap="0.4rem" alignItems="center">
                <StatusIndicator status={row.growthAlerts > 0 ? "cropAlert" : "normal"} />
                <Typography {...FARM_TYPOGRAPHY_STYLE}>{`${row.growthAlerts} ${alertsLabel}`}</Typography>
            </Stack>
        );
    } else {
        return (
            <Stack direction="row">
                {row.growth !== null && (
                    <Stack
                        direction="row"
                        gap="0.4rem"
                        alignItems="center"
                        bgcolor={row.isGrowthAlert ? "#FFEDA0" : undefined}
                        paddingLeft="0.5rem"
                        paddingRight="1rem"
                        paddingY="0.15rem"
                        borderRadius={1}
                    >
                        <CropIcon
                            sx={{
                                stroke: row.isGrowthAlert ? "#895A00" : "#00853B",
                                width: "0.9rem",
                                height: "0.9rem",
                            }}
                        />
                        <Typography {...FARM_TYPOGRAPHY_STYLE}>{`${Number(row.growth).toFixed(0)}%`}</Typography>
                    </Stack>
                )}
            </Stack>
        );
    }
};

const getCropLabel = (row: FarmInsightItem | FieldInsightItem) => {
    let label;

    if (row.type === "FARM") {
        if (row.crops.length > 1) {
            label = `${row.crops.length} Crops`;
        } else if (row.crops.length === 1) {
            label = row.crops[0];
        } else {
            label = null;
        }
    } else {
        label = row.crop;
    }

    return (
        <Typography
            {...(row.type === "FARM"
                ? FARM_TYPOGRAPHY_STYLE
                : row.field.status === FieldStatus.DRAFT
                  ? FIELD_DRAFT_TYPOGRAPHY_STYLE
                  : FIELD_TYPOGRAPHY_STYLE)}
        >
            {label}
        </Typography>
    );
};

const getAreaLabel = (row: FarmInsightItem | FieldInsightItem, ms: MeasureSystemUtils) => {
    const area = ms.getArea(Number(row.areaInHa)).toFixed(2);

    return (
        <Typography
            {...(row.type === "FARM"
                ? FARM_TYPOGRAPHY_STYLE
                : row.field.status === FieldStatus.DRAFT
                  ? FIELD_DRAFT_TYPOGRAPHY_STYLE
                  : FIELD_TYPOGRAPHY_STYLE)}
            textTransform="none"
        >
            {`${area} ${ms.getAreaUnits()}`}
        </Typography>
    );
};

const getExpandIconRotation = (row: MRT_Row<FarmInsightItem | FieldInsightItem>) => {
    const isRowExpanded = row.getIsExpanded();
    const isFarmEmpty = (row.original as FarmInsightItem).fields.length === 0;

    return isFarmEmpty ? 0 : isRowExpanded ? 180 : -90;
};

const getNextIrrigationDate = (row: FieldInsightItem, t: TFunction) => {
    const daysUntilNextIrrigation = calculateDaysUntilNextIrrigation(row);
    return daysUntilNextIrrigation !== null ? (
        <Typography
            {...(row.field.status === FieldStatus.DRAFT ? FIELD_DRAFT_TYPOGRAPHY_STYLE : FIELD_TYPOGRAPHY_STYLE)}
            style={{
                textAlign: "right",
                display: "inline-block",
                minWidth: "90px",
            }}
        >
            {daysUntilNextIrrigation === 0
                ? t("insights.irrigation_insights.today")
                : daysUntilNextIrrigation === 1
                  ? t("insights.irrigation_insights.tomorrow")
                  : daysUntilNextIrrigation >= 7
                    ? t("insights.irrigation_insights.more_than_7_days")
                    : `${daysUntilNextIrrigation} ${t("insights.irrigation_insights.in_days")}`}
        </Typography>
    ) : null;
};

const calculateDaysUntilNextIrrigation = (row: FieldInsightItem): number | null => {
    const timeUntilNextIrrigation = row?.timeUntilNextIrrigation;
    const orderState = row.field.order?.state;
    if (timeUntilNextIrrigation != null && orderState === OrderStatus.ACTIVE) {
        return Number(timeUntilNextIrrigation);
    }
    return null;
};

const isRowDefaultFarm = (row: FarmInsightItem | FieldInsightItem) => row.type === "FARM" && row.farm.isDefault;
